import dayjs from "dayjs";
import React, { useState, useRef, useEffect } from "react";
import ChevronIcon from "./SVGIcons/ChevronIcon";
import ClearIcon from "./SVGIcons/ClearIcon";
import CalendarIcon from "./SVGIcons/CalendarIcon";

const DateRangePicker = ({
  onClick,
  onDateClearButtonClick,
  resetCustomDate,
}) => {
  const maxDate = dayjs().startOf("day");
  const minDate = dayjs().subtract(1, "year").startOf("day");

  const [dates, setDates] = useState({ fromDate: null, toDate: null });
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);
  const [isHoveredFrom, setIsHoveredFrom] = useState(false);
  const [isHoveredTo, setIsHoveredTo] = useState(false);
  const [isHoveredClear, setIsHoveredClear] = useState(false);

  const [currentMonth, setCurrentMonth] = useState(
    dayjs().isBefore(minDate) ? minDate : dayjs(),
  );
  const [hoveredDate, setHoveredDate] = useState(null);

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const generateCalendarDays = (month) => {
    const start = month.startOf("month").startOf("week");
    const end = month.endOf("month").endOf("week");
    const days = [];
    let day = start;

    while (day.isBefore(end)) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  };

  const isDateInRange = (date) => {
    if (!dates.fromDate || !dates.toDate) return false;
    return date.isAfter(dates.fromDate) && date.isBefore(dates.toDate);
  };

  const isDateDisabled = (date, type) => {
    if (date.isBefore(minDate, "day")) return true;
    if (date.isAfter(maxDate, "day")) return true;

    if (type === "to" && dates.fromDate) {
      return date.isBefore(dates.fromDate, "day");
    }
    return false;
  };

  const renderCalendarGrid = (type) => {
    const days = generateCalendarDays(currentMonth);

    const handleDateClick = (date) => {
      if (isDateDisabled(date, type)) return;
      handleDateSelect(date, type);
    };

    return (
      <div style={styles.calendar}>
        <div style={styles.calendarHeader}>
          <button
            style={styles.navigationButton}
            onClick={() =>
              setCurrentMonth((curr) => {
                const prev = curr.subtract(1, "month");
                return prev.isBefore(minDate, "month") ? curr : prev;
              })
            }
          >
            ←
          </button>

          <span style={styles.monthYearText}>
            {currentMonth.format("MMMM YYYY")}
          </span>

          <button
            style={styles.navigationButton}
            onClick={() =>
              setCurrentMonth((curr) => {
                const next = curr.add(1, "month");
                return next.isAfter(maxDate, "month") ? curr : next;
              })
            }
          >
            →
          </button>
        </div>

        <div style={styles.weekDaysGrid}>
          {weekDays.map((day) => (
            <div key={day} style={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {days.map((day, index) => {
            const isSelected = dates[
              type === "from" ? "fromDate" : "toDate"
            ]?.isSame(day, "day");
            const isToday = day.isSame(dayjs(), "day");
            const isDisabled = isDateDisabled(day, type);
            const isInRange = isDateInRange(day);
            const isHovered = hoveredDate?.isSame(day, "day");

            return (
              <div
                key={index}
                style={{
                  ...styles.dayCell,
                  ...(isHovered && !isDisabled ? styles.dayCellHover : {}),
                  ...(isSelected ? styles.dayCellSelected : {}),
                  ...(isInRange ? styles.dayCellInRange : {}),
                  ...(isDisabled ? styles.dayCellDisabled : {}),
                  ...(isToday ? styles.dayCellToday : {}),
                }}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => !isDisabled && setHoveredDate(day)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {day.format("D")}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDropdown = (
    type,
    value,
    isOpen,
    setIsOpen,
    isHovered,
    setIsHovered,
  ) => (
    <div
      ref={type === "from" ? fromRef : toRef}
      style={styles.dropdownContainer}
    >
      <div
        style={{
          ...styles.dropdownHeader,
          ...(isHovered ? styles.dropdownHeaderHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.dropdownContent}>
          <span style={styles.dropdownTitle}>
            {type === "from" ? "From" : "To"}
          </span>
          <span style={styles.dropdownValue}>
            {value ? value.format("DD/MM/YY") : "Select date"}
          </span>
        </div>
        <div style={styles.dropdownActions}>
          <CalendarIcon />
          <div
            style={{
              ...styles.chevronIcon,
              ...(isOpen ? styles.chevronIconRotate : {}),
            }}
          >
            <ChevronIcon />
          </div>
        </div>
      </div>

      {isOpen && renderCalendarGrid(type)}
    </div>
  );

  // Reset on clear from parent
  useEffect(() => {
    setDates({ fromDate: null, toDate: null });
  }, [resetCustomDate]);

  useEffect(() => {
    if (isFromOpen) {
      setCurrentMonth(dates.fromDate || dayjs());
    }

    if (isToOpen) {
      if (dates.toDate) {
        setCurrentMonth(dates.toDate);
      } else {
        setCurrentMonth(dayjs());
      }
    }
  }, [isFromOpen, isToOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromRef.current && !fromRef.current.contains(event.target)) {
        setIsFromOpen(false);
      }
      if (toRef.current && !toRef.current.contains(event.target)) {
        setIsToOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateSelect = (date, type) => {
    const newDates = { ...dates };

    if (type === "from") {
      newDates.fromDate = date;
      newDates.toDate = null;
      setIsFromOpen(false);
      setIsToOpen(true);
    } else {
      newDates.toDate = date;
      setIsToOpen(false);
    }

    setDates(newDates);

    if (newDates.fromDate && newDates.toDate) {
      onClick({
        fromDate: newDates.fromDate.format("YYYY-MM-DD"),
        toDate: newDates.toDate.format("YYYY-MM-DD"),
      });
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setDates({ fromDate: null, toDate: null });
    onDateClearButtonClick();
  };

  return (
    <div style={styles.container}>
      {renderDropdown(
        "from",
        dates.fromDate,
        isFromOpen,
        setIsFromOpen,
        isHoveredFrom,
        setIsHoveredFrom,
      )}
      {renderDropdown(
        "to",
        dates.toDate,
        isToOpen,
        setIsToOpen,
        isHoveredTo,
        setIsHoveredTo,
      )}
      {(dates.fromDate || dates.toDate) && (
        <div style={styles.actionButtons}>
          <button
            onClick={handleClear}
            style={{
              ...styles.actionButton,
              ...(isHoveredClear ? styles.actionButtonHover : {}),
            }}
            onMouseEnter={() => setIsHoveredClear(true)}
            onMouseLeave={() => setIsHoveredClear(false)}
          >
            <ClearIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    position: "relative",
  },
  dropdownContainer: {
    position: "relative",
    width: "160px",
    border: "1px solid #ddd",
    borderRadius: "6px",
  },
  dropdownHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "border-color 0.2s ease",
  },
  dropdownHeaderHover: {
    borderColor: "#999",
  },
  dropdownContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  dropdownTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
  },
  dropdownValue: {
    fontSize: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dropdownActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  clearButton: {
    padding: "2px",
    background: "none",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    width: "16px",
    height: "16px",
    marginRight: "2px",
  },
  clearButtonHover: {
    backgroundColor: "#f0f0f0",
    color: "#333",
  },
  chevronIcon: {
    transition: "transform 0.2s ease",
    display: "flex",
  },
  chevronIconRotate: {
    transform: "rotate(180deg)",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
  },
  dayHeader: {
    textAlign: "center",
    fontSize: "12px",
    color: "#666",
    padding: "4px",
  },
  day: {
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    fontSize: "14px",
    transition: "background-color 0.2s ease",
  },
  dayHover: {
    backgroundColor: "#f0f0f0",
  },
  daySelected: {
    backgroundColor: "#e6f4ff",
    color: "#1890ff",
  },
  dayDisabled: {
    color: "#ccc",
    cursor: "not-allowed",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    marginLeft: "8px",
  },
  actionButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    cursor: "pointer",
    border: "none",
    backgroundColor: "rgba(255, 77, 79, 0.1)",
    transition: "all 0.2s ease",
    color: "#ff4d4f",
  },
  actionButtonHover: {
    backgroundColor: "rgba(255, 77, 79, 0.2)",
    color: "#ff1f1f",
  },
  calendar: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    padding: "16px",
    width: "280px",
  },
  calendarHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  monthYearText: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
  },
  navigationButton: {
    background: "none",
    border: "none",
    padding: "4px 8px",
    cursor: "pointer",
    color: "#666",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "background-color 0.2s ease",
  },
  navigationButtonHover: {
    backgroundColor: "#f0f0f0",
  },
  weekDaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    marginBottom: "8px",
  },
  weekDay: {
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "600",
    color: "#666",
    padding: "4px",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
  },
  dayCell: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "4px",
    fontSize: "14px",
    transition: "all 0.2s ease",
    position: "relative",
  },
  dayCellHover: {
    backgroundColor: "#f0f0f0",
  },
  dayCellSelected: {
    backgroundColor: "#e6f4ff",
    color: "#1890ff",
    fontWeight: "600",
  },
  dayCellInRange: {
    backgroundColor: "#f0f7ff",
    color: "#1890ff",
  },
  dayCellDisabled: {
    color: "#ccc",
    cursor: "not-allowed",
    backgroundColor: "transparent",
  },
  dayCellToday: {
    fontWeight: "600",
    color: "#1890ff",
  },
};
